import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, Edit, Calendar, X, Check, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HolidayScheduleManager = () => {
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    name: '',
    is_closed: true,
    open_hour: 16,
    close_hour: 23,
    preorder_start_hour: 11,
    message: ''
  });

  useEffect(() => {
    fetchHolidays();
  }, []);

  const fetchHolidays = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/admin/holidays`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(response.data);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.date || !formData.name) {
      toast.error('Date and name are required');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const data = {
        ...formData,
        open_hour: formData.is_closed ? null : formData.open_hour,
        close_hour: formData.is_closed ? null : formData.close_hour,
        preorder_start_hour: formData.is_closed ? null : formData.preorder_start_hour
      };

      if (editingHoliday) {
        await axios.put(`${API_URL}/api/admin/holidays/${editingHoliday.id}`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Holiday schedule updated');
      } else {
        await axios.post(`${API_URL}/api/admin/holidays`, data, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Holiday schedule created');
      }

      setDialogOpen(false);
      resetForm();
      fetchHolidays();
    } catch (error) {
      console.error('Failed to save holiday:', error);
      toast.error(error.response?.data?.detail || 'Failed to save holiday schedule');
    }
  };

  const handleDelete = async (holidayId) => {
    if (!window.confirm('Delete this holiday schedule?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/admin/holidays/${holidayId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Holiday schedule deleted');
      fetchHolidays();
    } catch (error) {
      console.error('Failed to delete holiday:', error);
      toast.error('Failed to delete holiday schedule');
    }
  };

  const handleEdit = (holiday) => {
    setEditingHoliday(holiday);
    setFormData({
      date: holiday.date,
      name: holiday.name,
      is_closed: holiday.is_closed,
      open_hour: holiday.open_hour || 16,
      close_hour: holiday.close_hour || 23,
      preorder_start_hour: holiday.preorder_start_hour || 11,
      message: holiday.message || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingHoliday(null);
    setFormData({
      date: '',
      name: '',
      is_closed: true,
      open_hour: 16,
      close_hour: 23,
      preorder_start_hour: 11,
      message: ''
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', { 
      weekday: 'short', 
      day: 'numeric', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const isPast = (dateStr) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return new Date(dateStr) < today;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="bg-primary/10 p-2 rounded-lg">
            <Calendar className="h-5 w-5 text-primary" />
          </div>
          <h2 className="text-lg font-bold">Holiday Schedules</h2>
        </div>
        <Button 
          size="sm" 
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Holiday
        </Button>
      </div>

      <p className="text-sm text-muted-foreground mb-4">
        Set special hours or closures for holidays. These override your regular operating hours.
      </p>

      {loading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : holidays.length === 0 ? (
        <div className="text-center py-8 bg-secondary/20 rounded-xl">
          <Calendar className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
          <p className="text-muted-foreground">No holiday schedules set</p>
        </div>
      ) : (
        <div className="space-y-2">
          <AnimatePresence>
            {holidays.map((holiday) => (
              <motion.div
                key={holiday.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className={`flex items-center justify-between p-4 rounded-xl border ${
                  isPast(holiday.date) ? 'bg-gray-50 opacity-60' : 'bg-white'
                }`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{holiday.name}</span>
                    {holiday.is_closed ? (
                      <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                        Closed
                      </span>
                    ) : (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                        Modified Hours
                      </span>
                    )}
                    {isPast(holiday.date) && (
                      <span className="text-xs text-muted-foreground">(Past)</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {formatDate(holiday.date)}
                    {!holiday.is_closed && (
                      <span className="ml-2">
                        • {holiday.open_hour}:00 - {holiday.close_hour}:00
                      </span>
                    )}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleEdit(holiday)}
                    className="h-8 w-8"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(holiday.id)}
                    className="h-8 w-8 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => {
        if (!open) resetForm();
        setDialogOpen(open);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingHoliday ? 'Edit Holiday Schedule' : 'Add Holiday Schedule'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="holiday-date">Date</Label>
              <Input
                id="holiday-date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-1.5"
              />
            </div>

            <div>
              <Label htmlFor="holiday-name">Holiday Name</Label>
              <Input
                id="holiday-name"
                placeholder="e.g., Christmas Day, New Year's Eve"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1.5"
              />
            </div>

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="is-closed">Completely Closed</Label>
                <p className="text-sm text-muted-foreground">
                  No orders or preorders accepted
                </p>
              </div>
              <Switch
                id="is-closed"
                checked={formData.is_closed}
                onCheckedChange={(checked) => setFormData({ ...formData, is_closed: checked })}
              />
            </div>

            {!formData.is_closed && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4 border-t pt-4"
              >
                <p className="text-sm text-muted-foreground">
                  Set modified operating hours for this day:
                </p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="preorder-hour">Preorder Opens</Label>
                    <Input
                      id="preorder-hour"
                      type="number"
                      min="0"
                      max="23"
                      value={formData.preorder_start_hour}
                      onChange={(e) => setFormData({ ...formData, preorder_start_hour: parseInt(e.target.value) || 0 })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="open-hour">Opens</Label>
                    <Input
                      id="open-hour"
                      type="number"
                      min="0"
                      max="23"
                      value={formData.open_hour}
                      onChange={(e) => setFormData({ ...formData, open_hour: parseInt(e.target.value) || 0 })}
                      className="mt-1.5"
                    />
                  </div>
                  <div>
                    <Label htmlFor="close-hour">Closes</Label>
                    <Input
                      id="close-hour"
                      type="number"
                      min="0"
                      max="23"
                      value={formData.close_hour}
                      onChange={(e) => setFormData({ ...formData, close_hour: parseInt(e.target.value) || 0 })}
                      className="mt-1.5"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            <div>
              <Label htmlFor="holiday-message">Custom Message (Optional)</Label>
              <Input
                id="holiday-message"
                placeholder="e.g., Merry Christmas! We're taking the day off."
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="mt-1.5"
              />
            </div>

            <div className="flex gap-2 pt-4">
              <Button variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                Cancel
              </Button>
              <Button onClick={handleSubmit} className="flex-1">
                {editingHoliday ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HolidayScheduleManager;
